/**
 * Auth API client.
 * Communicates with the server's OAuth endpoints.
 */

import { API_BASE, apiClient, apiRequest } from "@/lib/apiClient";
import type {
  FavoritedSpace,
  OAuthProviderInfo,
  AuthUser,
} from "@mind-sands/shared";

export type { FavoritedSpace, OAuthProviderInfo, AuthUser };

export const authApi = {
  /** Fetch list of enabled OAuth providers */
  async fetchProviders(): Promise<OAuthProviderInfo[]> {
    return apiClient.get("/api/auth/providers");
  },

  /** Fetch the current authenticated user */
  async fetchMe(): Promise<AuthUser | null> {
    const res = await apiRequest("/api/auth/me");
    if (res.status === 401) return null;
    if (!res.ok) throw new Error(`Failed to fetch user: ${res.statusText}`);
    const data = (await res.json()) as { user: AuthUser };
    return data.user;
  },

  /** Log out (delete session on server) */
  async logout(): Promise<void> {
    await apiClient.post("/api/auth/logout");
  },

  /** Get the URL to redirect to for starting an OAuth flow */
  getOAuthUrl(providerId: string): string {
    return `${API_BASE}/api/auth/${providerId}`;
  },

  /** List all favorited spaces */
  async listFavorites(): Promise<FavoritedSpace[]> {
    const res = await apiRequest("/api/favorites");
    if (res.status === 401) return [];
    if (!res.ok)
      throw new Error(`Failed to fetch favorites: ${res.statusText}`);
    const data = (await res.json()) as { favorites: FavoritedSpace[] };
    return data.favorites;
  },

  /** Add a space to favorites */
  async addFavorite(spaceId: string): Promise<void> {
    await apiClient.post("/api/favorites", { spaceId });
  },

  /** Remove a space from favorites */
  async removeFavorite(spaceId: string): Promise<void> {
    await apiClient.delete(`/api/favorites/${spaceId}`);
  },
};
